import { FiatOrCryptoAmount } from '@/components/atoms/Amount/Amount'
import { useFiatCurrencyStore } from '@/context/FiatCurrencyStore'
import { AppModals, useAppModal } from '@/context/ModalProvider'
import { DoubleState, GroupedBets, useDoubleStore } from '@/context/DoubleProvider'
import { useSession } from '@/context/SessionProvider'
import { Box, Flex, Text, Popover, PopoverBody, PopoverContent, PopoverTrigger } from '@chakra-ui/react'
import { useQuery } from '@apollo/client'
import { useAnimate } from 'framer-motion'
import { useEffect, useState } from 'react'
import { Image } from '@/components/HOC/Image'
import DoubleJackpotIcon from '@/public/assets/images/double/double-jackpot-icon.png'
import { colors } from '@/theme'
import { FiInfo } from 'react-icons/fi'
import { AnimatedAmount } from '../../../atoms/AnimatedAmount/AnimatedAmount'
import { gql } from '@/__generated__'
import { BigNumberInWei } from '@injectivelabs/utils'
import { getTokenByCurrency } from '@/services/token-service'
import { DoubleColor } from '@/__generated__/graphql'
import { useStoreWithEqualityFn } from 'zustand/traditional'
import { useShallow } from 'zustand/react/shallow'
import { SupportedCurrencies, TokenMoney } from '@ninjablaze/sdk-ts'
import { useTranslation } from 'next-i18next/pages'

export const DoubleJackpot = () => {
  const jackpotAmountUSDC = useDoubleStore((state) => state.jackpotAmountUSDC)
  const isRoundFinished = useDoubleStore((state) => state.isRoundFinished)
  const isJackpotDistribution = useDoubleStore((state) => state.isJackpotDistribution)
  const bets = useStoreWithEqualityFn(
    useDoubleStore,
    (state) => state.bets,
    (oldBets, newBets) => JSON.stringify(oldBets) === JSON.stringify(newBets)
  )

  const [isRendered, setRendered] = useState(false)
  const { account } = useSession()
  const times = [0, 0.1, 0.15, 0.2, 0.25, 0.35, 0.4]
  const rotateValues = [0, -10, 7, -7, 4, -1, 0]
  const [scope, animate] = useAnimate()
  const modal = useAppModal()
  const { t } = useTranslation()
  const isUsd = useFiatCurrencyStore((s) => s.isUsd())
  const usdcToFiat = useFiatCurrencyStore((s) => s.usdcToFiat)
  const getSymbol = useFiatCurrencyStore((s) => s.getSymbol)

  const usdcDenom = getTokenByCurrency(SupportedCurrencies.USDC).denom
  const humanJackpot = new TokenMoney(jackpotAmountUSDC, usdcDenom).humanBigNumber.toNumber()
  const jackpotDisplayValue = !isUsd ? usdcToFiat(humanJackpot) : humanJackpot

  const animation = async () => {
    for (let i = 0; i < rotateValues.length; i++) {
      scope.current && (await animate(scope.current, { rotate: rotateValues[i] + 'deg' }, { duration: times[i] }))
    }
  }

  useEffect(() => {
    isRendered && animation()
    !isRendered && setRendered(true)
  }, [jackpotAmountUSDC])

  useEffect(() => {
    if (isJackpotDistribution && isRoundFinished) {
      modal.openModalNew(AppModals.DoubleJackpot, {
        bets: getRoundPool(bets).toNumber(),
        userBets: getUserBets(bets, account?.injectiveAddress).toNumber(),
        jackpotPool: jackpotAmountUSDC,
      })
    }
  }, [isRoundFinished])

  return (
    <Flex
      pb={1}
      pt={1.5}
      pr={3}
      rounded={'lg'}
      alignItems={'center'}
      border={'1px solid'}
      borderColor={'ninja.400'}
      bg={'whiteAlpha.100'}
      h={12}
      bgGradient={colors.gradient.primary}
      zIndex={10}
    >
      <Image ref={scope} pos={'relative'} marginLeft={-8} top={-1} src={DoubleJackpotIcon} alt={''} />
      <Box lineHeight={1}>
        <Text fontWeight={700} fontSize={'xs'} mb={0.5}>
          {t('common.jackpot')}
        </Text>
        <AnimatedAmount
          suffix={!isUsd ? ` ${getSymbol()}` : undefined}
          amountProps={{
            iconSize: 5,
            wrapperStyles: {
              fontSize: 'xl',
            },
            currencyStyles: {
              color: 'ninja.100',
              display: !isUsd ? 'none' : undefined,
            },
            iconStyles: {
              display: !isUsd ? 'none' : undefined,
            },
            denom: usdcDenom,
          }}
          value={jackpotDisplayValue}
        />
      </Box>
      <JackpotHistory jackpotPool={jackpotAmountUSDC} />
    </Flex>
  )
}

const getRoundPool = (bets: GroupedBets) => {
  const blackUserBets = bets[DoubleColor.Black].reduce((prev, curr) => prev.plus(curr.amount), new BigNumberInWei(0))
  const redUserBets = bets[DoubleColor.Red].reduce((prev, curr) => prev.plus(curr.amount), new BigNumberInWei(0))
  const greenUserBets = bets[DoubleColor.Green].reduce((prev, curr) => prev.plus(curr.amount), new BigNumberInWei(0))

  return blackUserBets.plus(redUserBets).plus(greenUserBets)
}

const getUserBets = (bets: GroupedBets, address?: string) => {
  if (!address) {
    return new BigNumberInWei(0)
  }

  const blackUserBets = bets[DoubleColor.Black]
    .filter((bet) => bet.player === address)
    .reduce((prev, curr) => prev.plus(curr.amount), new BigNumberInWei(0))
  const redUserBets = bets[DoubleColor.Red]
    .filter((bet) => bet.player === address)
    .reduce((prev, curr) => prev.plus(curr.amount), new BigNumberInWei(0))
  const greenUserBets = bets[DoubleColor.Green]
    .filter((bet) => bet.player === address)
    .reduce((prev, curr) => prev.plus(curr.amount), new BigNumberInWei(0))

  return blackUserBets.plus(redUserBets).plus(greenUserBets)
}

const JackpotHistory = ({ jackpotPool }: { jackpotPool: string }) => {
  const { t } = useTranslation()
  const state = useDoubleStore(
    useShallow((state) => {
      if (state.state === DoubleState.FINISHED && !state.isRoundFinished) {
        return DoubleState.DRAWING
      }

      return state.state
    })
  )

  const { data } = useQuery(LAST_ROULETTE_JACKPOTS_QUERY, {
    skipPollAttempt: () => state === DoubleState.DRAWING,
  })

  const previousJackpot = tranformJackpotsHistory(data)

  return (
    <Popover isLazy={true} trigger={'click'} strategy="fixed">
      <PopoverTrigger>
        <Box as={'button'} rounded={'lg'} ml={3} cursor={'pointer'} bg={'whiteAlpha.100'} p={1.5}>
          <FiInfo />
        </Box>
      </PopoverTrigger>
      <PopoverContent width={{ base: '300px', md: '400px' }} top={2} bg={'ninja.600'} border={'none'} rounded={'xl'}>
        <PopoverBody p={5}>
          <Box bg={'ninja.700'} rounded={'xl'} py={2} px={4} mb={2}>
            <Text mb={0.5} fontWeight={700}>
              {t('double.current-jackpot')}
            </Text>
            <FiatOrCryptoAmount
              wrapperStyles={{
                fontSize: 'xl',
              }}
              iconSize={5}
              denom={getTokenByCurrency(SupportedCurrencies.USDC).denom}
              value={new TokenMoney(jackpotPool, getTokenByCurrency(SupportedCurrencies.USDC).denom)}
            />
          </Box>
          {previousJackpot && (
            <Box bg={'ninja.700'} rounded={'xl'} py={2} px={4} mb={3}>
              <Flex
                alignItems={{ base: 'start', md: 'center' }}
                flexDir={{ base: 'column', md: 'row' }}
                justifyContent={'space-between'}
                mb={{ base: 0.5, md: 0 }}
              >
                <Text mb={{ base: 0, md: 0.5 }} fontWeight={700} whiteSpace={'nowrap'}>
                  {t('double.previous-jackpot')}
                </Text>
                {/* <Text
                  fontWeight={700}
                  color={{ base: 'ninja.200', md: 'ninja.100' }}
                  fontSize={{ base: 'xs', md: 'sm' }}
                  textAlign={'end'}
                >
                  {new Date(parseInt(previousJackpot.drawTimeStamp)).toLocaleString('en-US', {
                    timeZone: 'UTC',
                  })}
                </Text> */}
              </Flex>
              <FiatOrCryptoAmount
                wrapperStyles={{
                  fontSize: 'xl',
                }}
                iconSize={5}
                denom={getTokenByCurrency(SupportedCurrencies.USDC).denom}
                value={new TokenMoney(previousJackpot.jackpot, getTokenByCurrency(SupportedCurrencies.USDC).denom)}
              />
            </Box>
          )}
          <Text fontSize={'xs'} color={'ninja.200'}>
            {t('double.jackpot-description')}
          </Text>
        </PopoverBody>
      </PopoverContent>
    </Popover>
  )
}

const tranformJackpotsHistory = (data: any) => {
  if (!data) {
    return null
  }

  return data.doubleJackpots.nodes.at(0)
}

const LAST_ROULETTE_JACKPOTS_QUERY = gql(`
  query GetDoubleJackpots {
    doubleJackpots(orderBy: ID_DESC, first: 1) {
      nodes {
        id
        startBlock
        drawTimeStamp
        jackpot
      }
    }
  }
`)
